import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { Helmet } from "react-helmet-async";

type WindowTitleContextType = {
  setWindowTitle: (title: string) => void;
  setWindowTitleSuffix: (title: string) => void;
};

const WindowTitleContext = createContext<WindowTitleContextType | undefined>(
  undefined,
);

export const WindowTitleProvider = ({ children }: { children: ReactNode }) => {
  const [windowTitle, setWindowTitle] = useState("");
  const [windowTitleSuffix, setWindowTitleSuffix] = useState("");

  const finalTitle = [windowTitle, windowTitleSuffix]
    .filter(Boolean)
    .join(" | ");

  return (
    <WindowTitleContext.Provider
      value={{
        setWindowTitle: (title) => setWindowTitle(title),
        setWindowTitleSuffix: (title) => setWindowTitleSuffix(title),
      }}
    >
      {finalTitle && (
        <Helmet>
          <title>{finalTitle}</title>
        </Helmet>
      )}
      {children}
    </WindowTitleContext.Provider>
  );
};

const useWindowTitleContext = () => {
  const context = useContext(WindowTitleContext);

  if (!context) {
    throw new Error(
      "useWindowTitleContext must be used within a WindowTitleProvider",
    );
  }

  return context;
};

export const WindowTitle = ({ title }: { title: string }) => {
  const { setWindowTitle } = useWindowTitleContext();

  useEffect(() => {
    setWindowTitle(title);
    return () => setWindowTitle("");
  }, [title, setWindowTitle]);

  return null;
};

export const WindowTitleSuffix = ({ title }: { title: string }) => {
  const { setWindowTitleSuffix } = useWindowTitleContext();

  useEffect(() => {
    setWindowTitleSuffix(title);
    return () => setWindowTitleSuffix("");
  }, [title, setWindowTitleSuffix]);

  return null;
};
